/**
 * Reserved field names are system fields, named in a way that
 * minimizes conflicts with user data in CSV/TSV files.  Currently
 * only used for the associated image field.
 */
export const RESERVED_FIELD_NAMES = {
  IMAGE: '<<Image>>',
}

/**
 * Numeric range.  Allowed combinations:
 *   [m, ∞)
 *   (m, ∞)
 *   (-∞, n]
 *   (-∞, n)
 *   [m, n]
 *   [m, n)
 *   (m, n]
 *   (m, n)
 */
export type NumericRange =
  | {
      minInclusive: number
      minExclusive?: undefined
      maxInclusive?: undefined
      maxExclusive?: undefined
    }
  | {
      minInclusive?: undefined
      minExclusive: number
      maxInclusive?: undefined
      maxExclusive?: undefined
    }
  | {
      minInclusive?: undefined
      minExclusive?: undefined
      maxInclusive: number
      maxExclusive?: undefined
    }
  | {
      minInclusive?: undefined
      minExclusive?: undefined
      maxInclusive?: number
      maxExclusive: number
    }
  | {
      minInclusive: number
      minExclusive?: undefined
      maxInclusive: number
      maxExclusive?: undefined
    }
  | {
      minInclusive: number
      minExclusive?: undefined
      maxInclusive?: number
      maxExclusive: number
    }
  | {
      minInclusive?: undefined
      minExclusive: number
      maxInclusive: number
      maxExclusive?: undefined
    }
  | {
      minInclusive?: undefined
      minExclusive: number
      maxInclusive?: number
      maxExclusive: number
    }

export type LabeledNumericRange = NumericRange & { label: string }

/**
 * Specifies numeric filtering configuration.  Currently, only pre-determined ranges
 * are supported, though this could be expanded in the future to allow user-specified
 * ranges instead (or in addition to).
 */
export type NumericFilterConfigData = {
  type: 'Numeric'
  ranges: LabeledNumericRange[]
}

/**
 * Categorical filtering configuration.  When the filter controls are rendered, the table
 * data for the specified column (field) is scanned, which populates the select control.
 */
export type CategoricalFilterConfigData = {
  type: 'Categorical'
}

/**
 * Matrix filters allow column headers from multiple tables to be treated as filter categories;
 * when the user selects one of these values (column headers), rows without truthy values in
 * that column are filtered out.
 */
export type MatrixFilterConfigData = {
  type: 'Matrix'
  filterName: string
}

export type FilterConfigData =
  | NumericFilterConfigData
  | CategoricalFilterConfigData
  | MatrixFilterConfigData

/**
 * Once the user has made filter selections, there is concrete filter data based on
 * the configuration; that is, it specifies specific numeric ranges and specific categories
 * with which to actually filter the data.
 */
export type FilterData =
  | (NumericRange & {
      type: 'Numeric'
    })
  | {
      type: 'Categorical'
      value: string
    }

/**
 * Currently permitted data types for table values.  Only strings and numbers are currently
 * used.
 */
export type FieldValue = string | number | boolean

/**
 * For the purposes of the catalog viewer, a field has a numeric index, a key, and a label.
 * This allows for grouping of fields (e.g. column groups).
 */
export type FieldData = {
  index: number
  key: string
  label: string
}

export type FieldConfigData = {
  name: string
  role?: 'Image Asset ID'
  filterConfig: FilterConfigData
}

/**
 * This type is duplicated from the `bam-frontend` repo; ideally there would be a common
 * place to store types, but..that's a monorepo discussion.  So for now, we gotta keep these
 * in sync!
 */
export type CatalogTableData = {
  id: string
  name: string
  description?: string
  /**
   * Optional text to be shown on the left side of the table, directly above it.
   */
  leftText?: string
  /**
   * Optional text to be shown on the left right of the table, directly above it.
   */
  rightText?: string
  fields: FieldConfigData[]
  rows: Array<Record<string, FieldValue>>
  /**
   * Keys of any fields that have enabled filters.  Filters appear in the order specified here.
   */
  enabledFilterKeys: string[]
}

/**
 * Asset shape for a catalog asset.  Note that most of this typing was inferred from inspection.
 */
export type CatalogAssetData = {
  asset_type: 'catalog'
  catalog: {
    image: string
    matrixFilterNames?: string[]
    tables: CatalogTableData[]
  }
  created: number
  description: string
  extension: 'catalog'
  fileSize: string
  fileType: 'Catalog'
  file_type: 'catalog'
  filename: string
  id: string
  key: string
  size: number
  tags: string[]
  title: string
  updated: number
  url: string
  userTags: string[]
  version: number
}

/**
 * Takes an array of labels that may have embedded section groupings (i.e. with a separator character)
 * and returns an array of either a single item or a section containing multiple items.  Note that order
 * is preserved; if section groupings are non-contiguous, all items will appear under the first occurrence.
 * The original index is also included with each item.
 * @example
 *   const labels = ['one', 'two | a', 'two | b', 'three', 'two | c', 'four']
 *   groupLabelsBySection(labels)  //
 *     // [
 *     //   { index: 0, key: 'one', label: 'one' },
 *     //   {
 *     //     section: 'two',
 *     //     items: [
 *     //       { index: 1, key: 'two | a', label: 'a' },
 *     //       { index: 2, key: 'two | b', label: 'b' },
 *     //       { index: 4, key: 'two | c', label: 'c' },
 *     //     ]
 *     //   },
 *     //   { index: 3, key: 'three', label: 'three' },
 *     //   { index: 5, key: 'four', key: 'four' },
 *     // ]
 */
export function groupLabelsBySection(
  labels: string[],
  options: {
    /**
     * String that separates sections; defaults to vertical bar ('|').  Note that only one level of
     * grouping is supported; only the first separator string is relevant, everything after that is
     * is ignored (i.e. "a | b | c" results in category "a" with one item, "b | c").
     */
    separator?: string
    /**
     * If true, all labels are trimmed.  Note that the `key` property will contain the original,
     * un-trimmed label, which may be necessary for retrieving by key.
     */
    trim?: boolean
    /**
     * Specify keys of fields you want to omit (e.g. title & description if you are handling them separately).
     */
    omit?: string[]
  } = {},
): Array<FieldData | { section: string; items: FieldData[] }> {
  const { trim = true, separator = '|', omit = [] } = options
  const sectionsByLabel: Record<string, FieldData[]> = {}
  return labels
    .map((label, index) => {
      const [first, ...rest] = label.split(separator)
      if (rest.length === 0) {
        return omit.includes(label)
          ? undefined
          : { index, key: label, label: trim ? first.trim() : first }
      } else {
        const section = trim ? first.trim() : first
        const items = sectionsByLabel[section] || []
        if (!omit.includes(label))
          items.push({
            index,
            key: label,
            label: trim ? rest.join('').trim() : rest.join(''),
          })
        if (sectionsByLabel[section]) return undefined
        sectionsByLabel[section] = items
        return { section, items }
      }
    })
    .filter((o) => o !== undefined)
}

/**
 * Given an array of field values, will extract all numeric data and attempt to construct
 * a collection of ranges (e.g. for use in a filter).
 *
 * Note that this very rarely results in a "pretty" result, but should make it clear how
 * the data can be edited to be pretty.
 */
export function autoGenerateNumericRanges(
  allValues: FieldValue[],
  options: {
    rangeCount: number
    numFormat: Intl.NumberFormatOptions
    algorithm: 'Range Partition'
  } = {
    rangeCount: 5,
    numFormat: { minimumFractionDigits: 0, maximumFractionDigits: 2 },
    algorithm: 'Range Partition',
  },
): LabeledNumericRange[] {
  // currently the algorithm only supports equal partitioning of the range; a more sophisticated algorithm
  // would be to ensure partitions contain a similar number of items (e.g. for a non-linear distribution)
  if (options.algorithm !== 'Range Partition')
    throw new Error(`unsupported algorithm: ${options.algorithm}`)
  const values = allValues.filter((v) => typeof v === 'number')
  if (values.length === 0) return []
  const min = Math.min(...values)
  const max = Math.max(...values)
  if (max - min === 0)
    return [
      {
        minInclusive: values[0],
        maxInclusive: values[0],
        label: values[0].toLocaleString(undefined, options.numFormat),
      },
    ]
  const rangeSize = (max - min) / options.rangeCount
  const ranges = Array.from({ length: options.rangeCount }).map((_, i) => {
    const minInclusive = min + i * rangeSize
    const maxInclusive = minInclusive + rangeSize
    const label = `${minInclusive.toLocaleString(
      undefined,
      options.numFormat,
    )} to ${maxInclusive.toLocaleString(undefined, options.numFormat)}`
    return { minInclusive, maxInclusive, label }
  })
  return ranges
}
